import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Brush, ReferenceLine
} from 'recharts';
import { Activity, TrendingUp, Filter, BarChart3, AlertTriangle, Zap, IndianRupee, Percent, Clock, Table2, ChevronLeft, ChevronRight, LayoutTemplate } from 'lucide-react';

interface RtcChartsProps {
  data: {
    data: any[];
    columns: string[];
    summary_stats?: any;
  } | any[];
  loading?: boolean;
  title?: string;
}

const RtcCharts: React.FC<RtcChartsProps> = ({ data: rawData, loading, title = "Simulation Results" }) => {
  const [viewMode, setViewMode] = useState<'profile' | 'custom'>('profile');
  const [selectedX, setSelectedX] = useState<string[]>(['Datetime']);
  const [selectedY, setSelectedY] = useState<string[]>(['Total_Load_Met_MW']);
  const [searchQuery, setSearchQuery] = useState('');
  const [brushRange, setBrushRange] = useState({ start: 0, end: 0 });
  const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { availableColumns, chartData } = React.useMemo(() => {
    if (!rawData) {
      return { availableColumns: [] as string[], chartData: [] as any[] };
    }

    let processedData: any[] = [];
    let cols: string[] = [];

    if (Array.isArray(rawData)) {
      if (rawData.length > 0) {
        cols = Object.keys(rawData[0]);
        processedData = [...rawData];
      }
    } else if ((rawData as any).data && (rawData as any).columns) {
      cols = (rawData as any).columns;
      processedData = [...(rawData as any).data];
    }

    if (cols.indexOf('Datetime') !== -1) {
      processedData.sort((a, b) => {
        const dateA = new Date(a.Datetime).getTime();
        const dateB = new Date(b.Datetime).getTime();

        if (!isNaN(dateA) && !isNaN(dateB)) {
          return dateA - dateB;
        }

        const parseDMY = (value: string) => {
          const parts = value.split(/[-/ :]/);
          if (parts.length >= 3) {
            return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])).getTime();
          }
          return 0;
        };

        return parseDMY(a.Datetime) - parseDMY(b.Datetime);
      });
    } else if (cols.indexOf('Time') !== -1) {
      processedData.sort((a, b) => (a.Time > b.Time ? 1 : -1));
    }

    const MAX_CHART_POINTS = 2000;
    if (processedData.length > MAX_CHART_POINTS) {
      const samplingInterval = Math.ceil(processedData.length / MAX_CHART_POINTS);
      processedData = processedData.filter((_, index) => index % samplingInterval === 0);
    }

    return { availableColumns: cols, chartData: processedData };
  }, [rawData]);

  // We use a ref for the range to access it inside the passive:false listener
  const rangeRef = useRef(brushRange);
  useEffect(() => { rangeRef.current = brushRange; }, [brushRange]);

  useEffect(() => {
    if (chartData.length > 0) {
      setBrushRange({ start: 0, end: chartData.length - 1 });
    }
  }, [chartData.length]);

  useEffect(() => {
    if (!availableColumns.length) return;

    setSelectedX(prev => {
      if (prev.some(col => availableColumns.indexOf(col) !== -1)) {
        return prev;
      }
      if (availableColumns.indexOf('Datetime') !== -1) {
        return ['Datetime'];
      }
      if (availableColumns.indexOf('Time') !== -1) {
        return ['Time'];
      }
      return prev;
    });

    setSelectedY(prev => {
      if (prev.some(col => availableColumns.indexOf(col) !== -1)) {
        return prev;
      }
      if (availableColumns.indexOf('Total_Load_Met_MW') !== -1) {
        return ['Total_Load_Met_MW'];
      }
      return prev;
    });
  }, [availableColumns]);

  const summaryStats = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return [];

    // Prioritize backend-calculated stats if available (Prevents browser crash/lag)
    const backendStats = (rawData as any)?.summary_stats;
    
    // Helper to generate the standardized ticket array from raw numbers
    const generateTickets = (stats: any) => [
        { 
            label: 'Net Revenue', 
            value: '₹' + Number(stats.net_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }),
            icon: <IndianRupee className="w-3 h-3" />,
            border: 'border-emerald-100', bg: 'bg-emerald-50', text: 'text-emerald-600'
        },
        { 
            label: 'Penalty', 
            value: '₹' + Number(stats.penalty_inr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }),
            icon: <AlertTriangle className="w-3 h-3" />,
            border: 'border-rose-100', bg: 'bg-rose-50', text: 'text-rose-600'
        },
        { 
            label: 'Daily PLF\nto Grid', 
            value: Number(stats.daily_plf || 0).toFixed(1),
            unit: '(%)',
            icon: <Percent className="w-3 h-3" />,
            border: 'border-[#E3F0F9]', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]'
        },
        { 
            label: 'Penalty Blocks', 
            value: stats.penalty_blocks || 0,
            icon: <Clock className="w-3 h-3" />,
            border: 'border-amber-100', bg: 'bg-amber-50', text: 'text-amber-600'
        },
        { 
            label: 'Total Energy\nTo Grid', 
            value: Number(stats.total_grid_energy || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }),
            unit: '(MWh)',
            icon: <Zap className="w-3 h-3" />,
            border: 'border-[#E3F0F9]', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]'
        },
        { 
            label: 'Total Energy\nto Exchange', 
            value: Number(stats.total_exchange_energy || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }),
            unit: '(MWh)',
            icon: <TrendingUp className="w-3 h-3" />,
            border: 'border-[#E3F0F9]', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]'
        },
        { 
            label: 'Average\nExchange Rate', 
            value: '₹' + Number(stats.avg_exchange_rate || 0).toFixed(2),
            icon: <IndianRupee className="w-3 h-3" />,
            border: 'border-slate-100', bg: 'bg-slate-50', text: 'text-slate-600'
        },
        { 
            label: 'Battery State\nof Charge', 
            value: (stats.soc_start !== undefined && stats.soc_end !== undefined) ? `${Number(stats.soc_start).toFixed(1)} → ${Number(stats.soc_end).toFixed(1)}` : 'N/A',
            icon: <Activity className="w-3 h-3" />,
            border: 'border-[#E3F0F9]', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]',
            unit: '(MWh)'
        },
        { 
            label: 'Yearly PLF (YTD)', 
            value: Number(stats.ytd_plf || 0).toFixed(1),
            unit: '(%)',
            icon: <Percent className="w-3 h-3" />,
            border: 'border-[#E3F0F9]', bg: 'bg-[#F2F8FC]', text: 'text-[#0066A3]'
        },
        { 
            label: 'Battery Utilization\nFactor', 
            value: Number(stats.batt_util || 0).toFixed(1),
            unit: '(%)',
            icon: <Activity className="w-3 h-3" />,
            border: 'border-emerald-100', bg: 'bg-emerald-50', text: 'text-emerald-600'
        }
    ];

    if (backendStats) {
        return generateTickets(backendStats);
    }

    // Fallback: Frontend-only calculation for older API responses or unsaved data
    const totalGridEnergy = chartData.reduce((acc: number, d: any) => acc + (d.Grid_Load_MWh || 0), 0);
    const totalExchangeEnergy = chartData.reduce((acc: number, d: any) => acc + (d.Exchange_Load_MWh || 0), 0);
    const avgExchangeRate = chartData.length > 0 
        ? chartData.reduce((acc: number, d: any) => acc + (d.Exchange_Rate || 0), 0) / chartData.length 
        : 0;

    const totalFullLoad = chartData.reduce((acc: number, d: any) => acc + (d.Full_Load_MWh || 0), 0);
    const dailyPLF = totalFullLoad > 0 ? (totalGridEnergy / totalFullLoad) * 100 : 0;
    const penaltyBlocks = chartData.filter((d: any) => d.Penalty === 1).length;

    const penaltyINR = chartData.reduce((acc: number, d: any) => {
        if (d.Penalty === 1) {
            const short = Math.max(0, (d.Required_Min_Load_MWh || 0) - (d.Grid_Load_MWh || 0));
            return acc + (short * (d.gt_100 || 4.25) * 1000);
        }
        return acc;
    }, 0);

      const netRevenue = chartData.reduce(
        (acc: number, d: any) => acc + (d.Total_Actual_Rev ?? d.Actual_Rev ?? 0),
        0
      );
    
    const firstRow = chartData[0];
    const lastRow = chartData[chartData.length - 1];
    const socCol = Object.keys(firstRow).find(k => k.toLowerCase().includes('soc') || k.toLowerCase().includes('state_of_charge')) || 'Bsoc_End_MWh';
    
    // Battery Utilization Factor fallback calculation
    const totalBattThroughput = chartData.reduce((acc: number, d: any) => acc + Math.abs(d.Batt_C_D_MW || 0), 0) / 4;
    const battUtilFactor = totalFullLoad > 0 ? (totalBattThroughput / totalFullLoad) * 100 : 0;

    return generateTickets({
        net_revenue: netRevenue,
        penalty_inr: penaltyINR,
        daily_plf: dailyPLF,
        ytd_plf: dailyPLF * 0.98,
        penalty_blocks: penaltyBlocks,
        total_grid_energy: totalGridEnergy,
        total_exchange_energy: totalExchangeEnergy,
        avg_exchange_rate: avgExchangeRate,
        soc_start: firstRow[socCol],
        soc_end: lastRow[socCol],
        batt_util: battUtilFactor
    });
  }, [chartData, rawData]);

  const yDomain: [any, any] = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return [0, 'auto'];
    let min = 0;
    let max = 0;

    const activeKeys = viewMode === 'profile'
      ? ['Grid_MW', 'Exchange_MW', 'Batt_C_D_MW', 'Solar_Power_MW', 'Wind_Power_MW']
      : selectedY;

    const visibleKeys = activeKeys.filter(k => hiddenMetrics.indexOf(k) === -1);
    const keysToUse = visibleKeys.length > 0 ? visibleKeys : activeKeys;

    chartData.forEach((d: any) => {
      keysToUse.forEach(k => {
        const v = Number(d[k]);
        if (!isNaN(v) && isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      });
    });

    let paddedMin = 0;
    if (min < 0) {
      const margin = Math.max(5, Math.abs(min) * 0.15);
      paddedMin = Math.floor(min - margin);
    }

    const maxMargin = Math.max(10, Math.abs(max) * 0.1);
    const paddedMax = Math.ceil(max + maxMargin);

    return [paddedMin, paddedMax];
  }, [chartData, viewMode, selectedY, hiddenMetrics]);

  const executeZoom = useCallback((deltaY: number, deltaX: number, isShift: boolean, clientX?: number) => {
    if (!chartData.length) return;
    
    const zoomIntensity = 0.15;
    const total = chartData.length;
    const { start: currentStart, end: currentEnd } = rangeRef.current;
    const currentRange = currentEnd - currentStart;

    const CHART_LEFT_OFFSET = 80; 
    const CHART_RIGHT_OFFSET = 30;
    
    let anchorRatio = 0.5;
    if (clientX !== undefined && chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        const availableWidth = rect.width - CHART_LEFT_OFFSET - CHART_RIGHT_OFFSET;
        const relativeX = clientX - rect.left - CHART_LEFT_OFFSET;
        anchorRatio = Math.max(0, Math.min(1, relativeX / availableWidth));
    }

    if (isShift || Math.abs(deltaX) > Math.abs(deltaY)) {
      const panSpeed = Math.max(1, Math.round(currentRange * 0.05));
      const delta = (deltaX || deltaY) > 0 ? panSpeed : -panSpeed;
      
      setBrushRange(prev => {
        let newStart = prev.start + delta;
        let newEnd = prev.end + delta;
        if (newStart < 0) { newEnd = Math.min(total - 1, newEnd - newStart); newStart = 0; }
        if (newEnd >= total) { newStart = Math.max(0, newStart - (newEnd - (total - 1))); newEnd = total - 1; }
        return { start: newStart, end: newEnd };
      });
    } else {
      const anchorValue = currentStart + (anchorRatio * currentRange);
      const zoomIn = deltaY < 0;
      const scale = zoomIn ? (1 - zoomIntensity) : (1 + zoomIntensity);
      
      let nextRange = currentRange * scale;
      nextRange = Math.max(5, Math.min(total, nextRange));
      
      let nextStart = Math.round(anchorValue - (anchorRatio * nextRange));
      let nextEnd = Math.round(nextStart + nextRange);

      if (nextStart < 0) {
        nextStart = 0;
        nextEnd = Math.min(total - 1, Math.round(nextRange));
      }
      if (nextEnd >= total) {
        nextEnd = total - 1;
        nextStart = Math.max(0, Math.round(nextEnd - nextRange));
      }

      setBrushRange({ start: nextStart, end: nextEnd });
    }
  }, [chartData.length]);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const onNativeWheel = (e: WheelEvent) => {
      // Always prevent default scroll when interacting with the chart component
      // Using capture phase guarantees we stop it before any inner component (like Recharts) 
      // or the browser's default scroll containers can handle it.
      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();
      
      // Execute our custom zoom/pan logic
      executeZoom(e.deltaY, e.deltaX, e.shiftKey, e.clientX);
    };

    // Use capture: true to intercept the wheel event as it descends the DOM tree
    el.addEventListener('wheel', onNativeWheel, { passive: false, capture: true });
    
    // Also disable touchmove scrolling on the chart
    const onTouchMove = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });

    return () => {
      el.removeEventListener('wheel', onNativeWheel, { capture: true } as EventListenerOptions);
      el.removeEventListener('touchmove', onTouchMove, { capture: true } as EventListenerOptions);
    };
  }, [executeZoom]);

  const handleLegendClick = (data: any) => {
    const key = data.dataKey || data.value;
    setHiddenMetrics(prev => 
      prev.indexOf(key) !== -1 ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap gap-4 px-2 mb-6">
        {payload.map((entry: any, index: number) => {
          const isHidden = hiddenMetrics.indexOf(entry.dataKey || entry.value) !== -1;
          return (
            <div 
              key={`item-${index}`}
              onClick={() => handleLegendClick(entry)}
              className="flex items-center gap-2 cursor-pointer transition-all hover:opacity-80"
            >
              <div 
                className="w-3 h-3 rounded-full shadow-sm border border-white"
                style={{ backgroundColor: isHidden ? '#e2e8f0' : entry.color }}
              />
              <span 
                className={`text-[10px] font-black tracking-tight whitespace-normal ${isHidden ? 'text-gray-300 line-through' : 'text-gray-900'}`}
                style={{ fontWeight: 1000 }}
              >
                {formatLabel(entry.value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const formatLabel = (str: string) => {
    let formatted = str.replace(/_/g, ' ').trim();
    const suffixes = ['MWh', 'MW', '%', 'INR', 'Rs', '₹'];
    for (const suffix of suffixes) {
      if (formatted.endsWith(' ' + suffix)) {
        formatted = formatted.substring(0, formatted.length - suffix.length - 1) + ` (${suffix})`;
        break;
      }
    }
    return formatted;
  };

  const toggleX = (col: string) => {
    setSelectedX(prev => prev.indexOf(col) !== -1 ? prev.filter(c => c !== col) : [...prev, col]);
  };

  const toggleY = (col: string) => {
    setSelectedY(prev => prev.indexOf(col) !== -1 ? prev.filter(c => c !== col) : [...prev, col]);
  };

  const customColors = ['#f59e0b', '#0084CF', '#10b981', '#ef4444', '#0066A3', '#ec4899', '#0084CF', '#0084CF', '#f43f5e', '#0084CF', '#f97316', '#84cc16'];

  const filteredColumns = availableColumns.filter(col => 
    col.toLowerCase().includes(searchQuery.toLowerCase()) || 
    formatLabel(col).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full min-h-[500px] flex flex-col items-center justify-center bg-white rounded-xl border border-gray-100 shadow-sm">
        <Activity className="w-8 h-8 text-amber-500 animate-pulse mb-3" />
        <p className="text-gray-950 font-black" style={{ fontWeight: 1000 }}>Building complex energy profile...</p>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-full min-h-[500px] flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
        <TrendingUp className="w-12 h-12 text-gray-300 mb-4" />
        <h3 className="text-gray-950 font-black mb-1" style={{ fontWeight: 1000 }}>No Simulation Results</h3>
        <p className="text-sm text-gray-950 font-black max-w-xs" style={{ fontWeight: 1000 }}>Run a simulation to view the RTC Energy Profile chart.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-1.5 lg:p-2">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <h3 className="text-[12px] font-black text-gray-900 truncate uppercase tracking-tight flex items-center gap-1.5" style={{ fontWeight: 1000 }}>
              <Zap className="w-3.5 h-3.5 text-amber-500" /> {title}
            </h3>
          </div>
          
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            <div className="flex bg-gray-100/80 p-0.5 rounded-lg border border-gray-200/50 shadow-inner">
              <button 
                onClick={() => setViewMode('profile')}
                className={`px-3 py-1 text-[9px] font-black rounded-md transition-all ${viewMode === 'profile' ? 'bg-white text-gray-900 shadow-sm border border-gray-100' : 'text-gray-500 hover:text-gray-700'}`}
                style={{ fontWeight: 1000 }}
              >
                PROFILE VIEW
              </button>
              <button 
                onClick={() => setViewMode('custom')}
                className={`px-3 py-1 text-[9px] font-black rounded-md transition-all ${viewMode === 'custom' ? 'bg-white text-gray-900 shadow-sm border border-gray-100' : 'text-gray-500 hover:text-gray-700'}`}
                style={{ fontWeight: 1000 }}
              >
                CUSTOM AXES
              </button>
            </div>
          </div>
        </div>

        {/* Summary Row */}
        <div className="mb-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 border-b border-gray-100 pb-3">
            {summaryStats.map((stat, idx) => (
                <div key={idx} className={`flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg border ${stat.border} ${stat.bg} shadow-sm hover:shadow-md transition-all cursor-default min-h-[42px]`}>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <div className={`p-1 rounded-md bg-white ${stat.text} shadow-sm shrink-0 flex items-center justify-center`}>
                            {stat.icon}
                        </div>
                        <div className="flex flex-col min-w-0">
                            {stat.label.split('\n').map((line: string, lIdx: number) => (
                                <span key={lIdx} className="text-[8px] font-black text-slate-700 uppercase tracking-tight leading-[1.15] truncate" style={{ fontWeight: 1000 }}>
                                    {line}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-baseline gap-0.5 shrink-0 ml-auto text-right pl-1">
                        <span className="text-[10px] sm:text-[11px] font-black text-gray-950 leading-none truncate" style={{ fontWeight: 1000 }}>
                            {stat.value}
                        </span>
                        {stat.unit && (
                            <span className="text-[7.5px] font-black text-gray-400 shrink-0 ml-0.5" style={{ fontWeight: 1000 }}>
                                {stat.unit}
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>

        <div className={`flex flex-col lg:flex-row gap-6 relative flex-1 min-h-0 ${viewMode === 'custom' ? 'items-stretch' : ''}`}>
          {/* Main Chart Area */}
          <div className="flex-1 min-w-0 h-full">
            <div 
                className="h-full w-full outline-none" 
                ref={chartContainerRef}
                style={{ overflow: 'hidden', touchAction: 'none', minHeight: '400px' }}
            >
              <ResponsiveContainer width="100%" height="100%" minHeight={400}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id="colorSolar" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#facc15" stopOpacity={0.6}/><stop offset="95%" stopColor="#facc15" stopOpacity={0.1}/></linearGradient>
                    <linearGradient id="colorWind" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/></linearGradient>
                    <linearGradient id="colorGrid" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4ade80" stopOpacity={0.6}/><stop offset="95%" stopColor="#4ade80" stopOpacity={0.1}/></linearGradient>
                    <linearGradient id="colorExchange" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1e293b" stopOpacity={0.6}/><stop offset="95%" stopColor="#1e293b" stopOpacity={0.1}/></linearGradient>
                    <linearGradient id="colorBattery" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f472b6" stopOpacity={0.6}/><stop offset="95%" stopColor="#f472b6" stopOpacity={0.1}/></linearGradient>
                    <linearGradient id="colorCustom" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#facc15" stopOpacity={0.6}/><stop offset="95%" stopColor="#facc15" stopOpacity={0.1}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" vertical={true} stroke="#DFE6EE" strokeOpacity={0.6} />
                  <XAxis 
                    dataKey={viewMode === 'profile' ? 'Datetime' : (selectedX[0] || 'Datetime')} 
                    fontSize={9} 
                    tick={{ fill: '#000000', fontWeight: 700 }} 
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={false}
                    minTickGap={60}
                    tickFormatter={(val) => {
                      try {
                        const d = new Date(val);
                        return isNaN(d.getTime()) ? val : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                      } catch { return val; }
                    }}
                  />
                  <YAxis 
                    width={60}
                    fontSize={9} 
                    tick={{ fill: '#000000', fontWeight: 700 }} 
                    tickFormatter={(val) => val >= 1000 ? (val/1000).toFixed(1) + 'k' : (val <= -1000 ? (val/1000).toFixed(1) + 'k' : val)}
                    axisLine={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                    tickLine={false}
                    domain={yDomain}
                    label={{ 
                      value: viewMode === 'profile' ? 'Power (MW)' : formatLabel(selectedY[0] || 'Metric'), 
                      angle: -90, 
                      position: 'insideLeft', 
                      fontSize: 10, 
                      fontWeight: 900, 
                      offset: 15 
                    }}
                  />
                  <ReferenceLine y={0} stroke="#9AA8B8" strokeDasharray="3 3" strokeWidth={1.5} />
                  <Tooltip 
                    formatter={(value: any, name: any) => [typeof value === 'number' ? value.toFixed(2) : value, formatLabel(name)]}
                    labelFormatter={(label: any) => {
                      try {
                        const d = new Date(label);
                        return isNaN(d.getTime()) ? `Time: ${label}` : `Time: ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
                      } catch { return `Time: ${label}`; }
                    }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.25)', 
                      borderRadius: '8px', 
                      border: '1px solid rgba(255, 255, 255, 0.65)', 
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      padding: '10px 12px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }}
                    cursor={{ stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '4 4' }}
                    itemStyle={{ fontWeight: 800, fontSize: '11px' }}
                    labelStyle={{ fontWeight: 1000, marginBottom: '8px', color: '#16202B' }}
                  />
                  <Legend verticalAlign="top" align="left" height={70} content={renderLegend} />
                  
                  {viewMode === 'profile' ? (
                    <>
                      <Area type="linear" dataKey="Grid_MW" name="Grid (MW)" stroke="#22c55e" strokeWidth={2} fillOpacity={0.3} fill="url(#colorGrid)" hide={hiddenMetrics.indexOf('Grid_MW') !== -1} connectNulls />
                      <Area type="linear" dataKey="Exchange_MW" name="Exchange (MW)" stroke="#0f172a" strokeWidth={1.5} fillOpacity={0.2} fill="url(#colorExchange)" hide={hiddenMetrics.indexOf('Exchange_MW') !== -1} connectNulls />
                      <Area type="linear" dataKey="Batt_C_D_MW" name="Battery Charge/Discharge (MW)" stroke="#ec4899" strokeWidth={2} fillOpacity={0.3} fill="url(#colorBattery)" hide={hiddenMetrics.indexOf('Batt_C_D_MW') !== -1} connectNulls />
                      <Area type="linear" dataKey="Solar_Power_MW" name="Solar Power (MW)" stroke="#eab308" strokeWidth={2} fill="none" hide={hiddenMetrics.indexOf('Solar_Power_MW') !== -1} connectNulls />
                      <Area type="linear" dataKey="Wind_Power_MW" name="Wind Power (MW)" stroke="#3b82f6" strokeWidth={2} fill="none" hide={hiddenMetrics.indexOf('Wind_Power_MW') !== -1} connectNulls />
                    </>
                  ) : (
                    selectedY.map((col, idx) => (
                    <Area 
                      key={col} 
                      type="linear" 
                      dataKey={col} 
                      name={formatLabel(col)} 
                      stroke={customColors[idx % customColors.length]} 
                      strokeWidth={2}
                      fillOpacity={0.8} 
                      fill={`url(#colorCustom)`}
                      hide={hiddenMetrics.indexOf(col) !== -1}
                      connectNulls
                    />
                    ))
                  )}
                  
                  <Brush 
                    dataKey={viewMode === 'profile' ? 'Datetime' : (selectedX[0] || 'Datetime')} 
                    height={25} 
                    stroke="#9AA8B8" 
                    fill="#F2F8FC"
                    startIndex={brushRange.start}
                    endIndex={brushRange.end}
                    onChange={(range: any) => setBrushRange({ start: range.startIndex, end: range.endIndex })}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sidebar for Custom Axes */}
          {viewMode === 'custom' && (
            <div className="relative h-full flex flex-shrink-0">
               {isSidebarCollapsed && (
                  <button 
                    onClick={() => setIsSidebarCollapsed(false)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-white border border-gray-200 rounded-l-lg shadow-md hover:bg-amber-50 text-amber-600 transition-all"
                    title="Expand Data Fields"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
               )}
               <div className={`transition-all duration-300 ease-in-out flex flex-col bg-gray-50/50 rounded-xl border border-gray-200 overflow-hidden shadow-inner ${isSidebarCollapsed ? 'w-0 opacity-0' : 'w-full lg:w-[280px]'}`}>
                  <div className="px-4 py-3 bg-white border-b border-gray-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest" style={{ fontWeight: 1000 }}>Data Fields</span>
                      <button onClick={() => setIsSidebarCollapsed(true)} className="p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search columns..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full px-8 py-1.5 text-[10px] bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-amber-500 font-black"
                        />
                      <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden flex flex-col p-2 gap-2">
                    {/* Y-Values Section (Top Half) */}
                    <div className="flex-[3] flex flex-col min-h-0 bg-white/40 rounded-xl border border-gray-200/50 p-2.5 shadow-sm">
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-[9px] font-black text-amber-700 uppercase tracking-widest" style={{ fontWeight: 1000 }}>Selected Y-Metrics</span>
                        <span className="text-[8px] font-black text-gray-950 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100" style={{ fontWeight: 1000 }}>{selectedY.length} Selected</span>
                      </div>
                      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-0.5">
                        {filteredColumns.map(col => (
                          <div 
                            key={`y-${col}`} 
                            onClick={() => toggleY(col)} 
                             className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-all border ${selectedY.indexOf(col) !== -1 ? 'bg-amber-50 border-amber-200/50 shadow-sm' : 'hover:bg-white border-transparent'}`}
                          >
                             <div className={`w-3.5 h-3.5 rounded-md border-2 flex items-center justify-center transition-all ${selectedY.indexOf(col) !== -1 ? 'bg-amber-500 border-amber-600 scale-110' : 'bg-white border-gray-200'}`}>
                               {selectedY.indexOf(col) !== -1 && <div className="w-1.5 h-1.5 bg-white rounded-sm" />}
                            </div>
                             <span className={`text-[10.5px] truncate ${selectedY.indexOf(col) !== -1 ? 'font-black text-amber-950' : 'text-gray-950 font-black'}`} style={{ fontWeight: selectedY.indexOf(col) !== -1 ? 1000 : 900 }}>{formatLabel(col)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* X-Axis Section (Bottom Half) */}
                    <div className="flex-[2] flex flex-col min-h-0 bg-white/40 rounded-xl border border-gray-200/50 p-2.5 shadow-sm">
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-[9px] font-black text-[#1C4D88] uppercase tracking-widest" style={{ fontWeight: 1000 }}>Chart Axis (X)</span>
                      </div>
                      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-0.5">
                        {filteredColumns.map(col => (
                          <div 
                            key={`x-${col}`} 
                            onClick={() => toggleX(col)} 
                             className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-all border ${selectedX.indexOf(col) !== -1 ? 'bg-[#F2F8FC] border-[#C2DFF1]/50 shadow-sm' : 'hover:bg-white border-transparent'}`}
                          >
                             <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-all ${selectedX.indexOf(col) !== -1 ? 'bg-[#0084CF] border-[#0066A3] scale-110' : 'bg-white border-gray-200'}`}>
                               {selectedX.indexOf(col) !== -1 && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                            </div>
                             <span className={`text-[10.5px] truncate ${selectedX.indexOf(col) !== -1 ? 'font-black text-[#0D2747]' : 'text-gray-950 font-black'}`} style={{ fontWeight: selectedX.indexOf(col) !== -1 ? 1000 : 900 }}>{formatLabel(col)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
               </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RtcCharts);
